<script lang="ts">
  // Period filter: [◀] [This month ▾] [▶]. The menu lists the period presets and
  // a custom from/to range. Arrows shift the period (offset ≤ 0).
  import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Check } from '@lucide/svelte';
  import Popover from './Popover.svelte';
  import Button from './Button.svelte';
  import { t } from '../i18n.svelte';
  import { PERIOD_KEYS, canNavigate, resolvePeriod, type PeriodKey } from '../periods';
  import { account } from '../stores/account.svelte';
  import { today } from '../format';
  import '../messages/ui';

  interface Props {
    key?: PeriodKey;
    offset?: number;
    from?: string | null;
    to?: string | null;
    options?: PeriodKey[];
    size?: 'sm' | 'md';
    onchange?: (value: { key: PeriodKey; offset: number; from: string | null; to: string | null }) => void;
  }
  let { key = $bindable('month'), offset = $bindable(0), from = $bindable(null), to = $bindable(null), options = PERIOD_KEYS, size = 'md', onchange }: Props = $props();

  let open = $state(false);
  let anchor: HTMLElement | null = $state(null);
  let draftFrom = $state('');
  let draftTo = $state('');

  const range = $derived(resolvePeriod(key, { offset, work: account.work, from, to }));
  const navigable = $derived(canNavigate(key));

  function emit() {
    onchange?.({ key, offset, from, to });
  }
  function choose(k: PeriodKey) {
    if (k === 'custom') return;
    key = k;
    offset = 0;
    open = false;
    emit();
  }
  function shift(by: number) {
    offset = Math.min(0, offset + by);
    emit();
  }
  function applyCustom() {
    if (!draftFrom || !draftTo) return;
    key = 'custom';
    offset = 0;
    from = draftFrom <= draftTo ? draftFrom : draftTo;
    to = draftFrom <= draftTo ? draftTo : draftFrom;
    open = false;
    emit();
  }
  $effect(() => {
    if (open) {
      draftFrom = from || today().slice(0, 8) + '01';
      draftTo = to || today();
    }
  });
</script>

<div class="pp {size}">
  {#if navigable}
    <button type="button" class="arrow" aria-label={t('period.previous')} onclick={() => shift(-1)}><ChevronLeft size={16} /></button>
  {/if}
  <button type="button" class="pill" bind:this={anchor} onclick={() => (open = !open)} aria-expanded={open}>
    <CalendarDays size={15} />
    <span class="lbl">{range.label}</span>
    <ChevronDown size={14} />
  </button>
  {#if navigable}
    <button type="button" class="arrow" aria-label={t('period.next')} disabled={offset >= 0} onclick={() => shift(1)}><ChevronRight size={16} /></button>
  {/if}
</div>

<Popover bind:open {anchor} placement="bottom-start" width="280px">
  <div class="menu">
    {#each options.filter((k) => k !== 'custom') as k (k)}
      <button type="button" class="opt" class:sel={k === key} onclick={() => choose(k)}>
        <span>{t(`period.${k}`)}</span>
        {#if k === key}<Check size={15} />{/if}
      </button>
    {/each}
    {#if options.includes('custom')}
      <div class="custom">
        <span class="custom-title">{t('period.custom')}</span>
        <div class="dates">
          <input class="control" type="date" bind:value={draftFrom} aria-label={t('ui.from')} />
          <input class="control" type="date" bind:value={draftTo} aria-label={t('ui.to')} />
        </div>
        <Button variant="primary" size="sm" block onclick={applyCustom}>{t('common.apply')}</Button>
      </div>
    {/if}
  </div>
</Popover>

<style>
  .pp { display: inline-flex; align-items: center; gap: var(--space-1); min-width: 0; }
  .pill {
    display: inline-flex; align-items: center; gap: var(--space-2);
    height: var(--control-h); padding: 0 var(--space-3);
    min-width: 0;
    border: 1.5px solid var(--brand); border-radius: var(--radius-pill);
    background: var(--brand-soft); color: var(--brand);
    font-size: var(--text-sm); font-weight: var(--weight-semibold); white-space: nowrap;
  }
  .sm .pill { height: var(--control-h-sm); font-size: var(--text-sm); padding: 0 var(--space-2_5); }
  .pill:hover { background: var(--brand-tint); }
  .pill:focus-visible { outline: none; box-shadow: var(--ring); }
  .pill :global(svg) { color: var(--brand); flex-shrink: 0; }
  .lbl { text-transform: capitalize; max-width: 220px; overflow: hidden; text-overflow: ellipsis; }
  .arrow {
    width: 30px; height: 30px; border-radius: var(--radius-full); border: 0;
    display: grid; place-items: center;
    background: transparent; color: var(--text-2);
    flex-shrink: 0;
  }
  .arrow:hover:not(:disabled) { background: var(--surface-hover); color: var(--text); }
  .arrow:focus-visible { outline: none; box-shadow: var(--ring); }
  .arrow:disabled { opacity: 0.35; }
  .menu { padding: var(--space-1); display: flex; flex-direction: column; }
  .opt { display: flex; align-items: center; justify-content: space-between; padding: var(--space-2) var(--space-2_5); border: 0; border-radius: var(--radius-xs); background: transparent; color: var(--text); font-size: var(--text-base); text-align: left; }
  .opt:hover, .opt:focus-visible { background: var(--surface-hover); outline: none; }
  .opt.sel { color: var(--brand); font-weight: var(--weight-semibold); }
  .custom { border-top: 1px solid var(--border); margin-top: var(--space-1); padding: var(--space-2_5) var(--space-1_5) var(--space-1); display: flex; flex-direction: column; gap: var(--space-2); }
  .custom-title { font-size: var(--text-2xs); text-transform: uppercase; letter-spacing: var(--tracking-caps); font-weight: var(--weight-semibold); color: var(--text-3); }
  .dates { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-1_5); }
  .dates .control { padding: 0 var(--space-2); font-size: var(--text-sm); }
</style>
